import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, useIntl } from 'react-intl';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { styled } from '@mui/system';

import MenuList from './MenuList';
import Option from './Option';
import NoOptionsMessage from './NoOptionsMessage';
import Control from './Control';
import Placeholder from './Placeholder';
import SingleValue from './SingleValue';
import ValueContainer from './ValueContainer';
import MultiValue from './MultiValue';
import DropdownIndicator from './DropdownIndicator';
import { dropdownIndicatorStyle, menuStyle } from './ReactSelectWrappedStyle';
import messages from './messages';

const StyledSelectWrapper = styled('div')(({ theme }) => ({
	'& .input': {
		display: 'flex',
	},
	'& .valueContainer': {
		display: 'flex',
		flex: 1,
		alignItems: 'center',
	},
	'& .chip': {
		margin: `${theme.spacing(0.5)} ${theme.spacing(0.25)}`,
	},
	'& .chipFocused': {
		backgroundColor: 'lightgrey',
	},
	'& .noOptionsMessage': {
		fontSize: 16,
		padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
	},
	'& .singleValue': {
		fontSize: 16,
	},
	'& .placeholder': {
		position: 'absolute',
		left: 2,
		fontSize: 16,
		fontWeight: 200,
	},
	'& .MuiInputBase-input': {
		display: 'flex',
		justifyContent: 'space-between',
	},
}));

const components = {
	Option,
	Control,
	NoOptionsMessage,
	Placeholder,
	SingleValue,
	MultiValue,
	ValueContainer,
	MenuList,
	IndicatorSeparator: null,
	DropdownIndicator,
};

const ReactSelectWrapped = ({
	deleteDropdownIndicator = false,
	disabled = false,
	fastFilter = false,
	hasEmpty = false,
	innerRef,
	isLoading = false,
	loadOptions,
	maxSearchResults,
	options = [],
	style,
	value,
	windowing = false,
	dataTestId,
	getOptionLabel = (option) => option.label,
	fastFilterSearchValue = false,
	...other
}) => {
	const { formatMessage } = useIntl();
	const [refreshing, setRefreshing] = useState(false);

	const loadOptionsFastFilter = useCallback(
		(input, callback) => {
			setRefreshing(true);
			setTimeout(() => {
				const filterValueLowerCase = input.toLowerCase();
				let optionsFiltered = options;
				if (filterValueLowerCase) {
					optionsFiltered = optionsFiltered.filter((option) => {
						let searchableString = getOptionLabel(option);
						if (fastFilterSearchValue) {
							const optionValue =
								typeof option.value === 'object'
									? Object.values(option.value).join(' ')
									: option.value;
							searchableString = `${searchableString} ${optionValue}`;
						}
						return (
							searchableString &&
							searchableString.toLowerCase().indexOf(filterValueLowerCase) !==
								-1
						);
					});
					optionsFiltered = maxSearchResults
						? optionsFiltered.slice(0, maxSearchResults - 1)
						: optionsFiltered;
				}
				callback(optionsFiltered);
				setRefreshing(false);
			});
		},
		[options, getOptionLabel, maxSearchResults, fastFilterSearchValue],
	);

	const SelectComponent = fastFilter || loadOptions ? AsyncSelect : Select;

	const finalComponents = { ...components };
	if (!windowing) delete finalComponents.MenuList;
	if (deleteDropdownIndicator) finalComponents.DropdownIndicator = null;
	let placeholder = formatMessage(messages.lblPlaceholder);
	if (hasEmpty) {
		const emptyOption = options.find(
			(option) => option.value === null || option.value === undefined,
		);
		if (emptyOption) placeholder = emptyOption.label;
	}

	return (
		<StyledSelectWrapper style={style} data-testid={dataTestId}>
			<SelectComponent
				styles={{
					menu: (base) => ({ ...base, ...menuStyle }),
					dropdownIndicator: (base) => ({
						...base,
						...dropdownIndicatorStyle,
					}),
				}}
				classes={{}} // need this
				components={finalComponents}
				filterOption={fastFilter ? null : undefined}
				loadOptions={fastFilter ? loadOptionsFastFilter : loadOptions}
				isLoading={isLoading || refreshing}
				defaultOptions={
					maxSearchResults ? options.slice(0, maxSearchResults - 1) : options
				}
				options={fastFilter || loadOptions ? undefined : options}
				value={
					typeof value === 'object'
						? value
						: options.find((option) => option.value === value) || null
				}
				getOptionLabel={getOptionLabel}
				shortRow={windowing}
				isDisabled={disabled}
				placeholder={placeholder}
				ref={innerRef}
				menuShouldScrollIntoView={false}
				captureMenuScroll={!windowing && undefined}
				{...other}
			/>
		</StyledSelectWrapper>
	);
};

ReactSelectWrapped.propTypes = {
	style: PropTypes.object,
	intl: PropTypes.object.isRequired,
	options: PropTypes.array,
	fastFilter: PropTypes.bool,
	windowing: PropTypes.bool,
	value: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
		PropTypes.array,
		PropTypes.bool,
		PropTypes.object,
	]),
	disabled: PropTypes.bool,
	hasEmpty: PropTypes.bool,
	getOptionLabel: PropTypes.func,
	maxSearchResults: PropTypes.number,
	deleteDropdownIndicator: PropTypes.bool,
	innerRef: PropTypes.func,
	fastFilterSearchValue: PropTypes.bool,
	isLoading: PropTypes.bool,
	loadOptions: PropTypes.func,
	dataTestId: PropTypes.string,
};

export default React.memo(injectIntl(ReactSelectWrapped));
